import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { client } from '@frontend/features/common/services';
import type { Candidate } from './AddSelection';
import { AddDestinationHotel } from './AddDestinationHotel';
import { DestinationPicker } from './DestinationPicker';
import { catalogAdminKey, catalogAdminPath, editPayload, isSelectionConflict, SelectionPage, SelectionRow } from './queries';
import {
  membershipOffsetLimit,
  membershipPageSize,
  priorityPreview,
  validPriority,
  validPriorityReason,
} from './priorityOrder';

export function DestinationOrder() {
  const { t } = useTranslation();
  const cache = useQueryClient();
  const [destination, setDestination] = useState<Candidate | null>(null);
  const [included, setIncluded] = useState(true);
  const [adding, setAdding] = useState(false);
  const [mode, setMode] = useState<'priority' | 'add' | 'deactivate' | 'reactivate'>('priority');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<SelectionRow | null>(null);
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
  const [rank, setRank] = useState('');
  const [reason, setReason] = useState('');
  const [saved, setSaved] = useState(false);
  const [pendingChange, setPendingChange] = useState<(() => void) | null>(null);
  const inFlight = useRef(false);
  const dirty = !!selected && (mode !== 'priority' || rank !== String(selected.rank) || reason.length > 0);
  const changed = !!selected && validPriority(rank) && (mode !== 'priority' || Number(rank) !== selected.rank);
  const previewing = changed && mode === 'priority';
  const page = useQuery({
    queryKey: [...catalogAdminKey, 'order-memberships', destination?.id, included, offset],
    queryFn: async () =>
      (
        await client.get<SelectionPage>('hotel-catalog-admin/hotel-memberships', {
          params: { geo_place_id: destination!.id, enabled: String(included), limit: membershipPageSize, offset },
        })
      ).data,
    enabled: !!destination && !selected && !adding,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const save = useMutation({
    mutationFn: async (row: SelectionRow) => {
      if (!changed || !validPriorityReason(reason)) throw new Error('Invalid priority edit');
      return client.put(
        'hotel-catalog-admin/hotel-memberships',
        editPayload(
          'hotel-memberships',
          row,
          mode === 'priority' ? row.enabled : mode !== 'deactivate',
          mode === 'deactivate' ? row.rank! : Number(rank),
          reason,
        ),
      );
    },
    retry: false,
    onSuccess: async (_, row) => {
      setSelected(null);
      setSelectedCandidate(null);
      setAdding(false);
      setIncluded(mode === 'priority' ? row.enabled : mode !== 'deactivate');
      setReason('');
      setOffset(0);
      setSaved(true);
      await cache.invalidateQueries(catalogAdminKey);
    },
    onSettled: () => {
      inFlight.current = false;
    },
  });
  const resetEditor = () => {
    setAdding(false);
    setMode('priority');
    setSelected(null);
    setSelectedCandidate(null);
    setReason('');
    setRank('');
    setSaved(false);
    save.reset();
  };
  const change = (action: () => void) => {
    if (inFlight.current) return;
    if (dirty) setPendingChange(() => action);
    else action();
  };
  const choose = (row: SelectionRow, nextMode: typeof mode = 'priority', candidate: Candidate | null = null) =>
    change(() => {
      setAdding(false);
      setMode(nextMode);
      setSelected(row);
      setSelectedCandidate(candidate);
      setRank(String(row.rank));
      setReason('');
      setSaved(false);
      save.reset();
    });
  const rows = page.data?.data ?? [];
  const preview = priorityPreview(rows, mode === 'priority' ? selected : null, rank);
  const complete = offset === 0 && page.data?.meta.has_more === false;
  const previewPosition = selected ? preview.findIndex((row) => row.hotel_id === selected.hotel_id) + 1 : 0;
  const oldPosition = selected ? rows.findIndex((row) => row.hotel_id === selected.hotel_id) + 1 : 0;
  const blocked = save.isLoading || save.isError;
  return (
    <Stack spacing={2}>
      <Typography color="text.secondary">{t('hotelDestinationOrder.subtitle')}</Typography>
      <DestinationPicker
        value={destination}
        disabled={blocked}
        onChange={(next) =>
          change(() => {
            resetEditor();
            setDestination(next);
            setOffset(0);
          })
        }
      />
      {!destination && <Typography color="text.secondary">{t('hotelDestinationOrder.chooseDestination')}</Typography>}
      {saved && <Alert severity="success">{t('hotelDestinationMembership.saved')}</Alert>}
      {destination && (
        <>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {[true, false].map((value) => (
              <Button
                key={String(value)}
                variant={included === value ? 'contained' : 'outlined'}
                aria-pressed={included === value}
                disabled={blocked}
                onClick={() =>
                  change(() => {
                    resetEditor();
                    setIncluded(value);
                    setOffset(0);
                  })
                }
              >
                {t(value ? 'hotelDestinationMembership.included' : 'hotelDestinationMembership.disabled')}
              </Button>
            ))}
            <Button
              disabled={blocked || adding || page.isFetching || page.isError}
              variant="outlined"
              onClick={() =>
                change(() => {
                  resetEditor();
                  setAdding(true);
                })
              }
            >
              {t('hotelDestinationMembership.add')}
            </Button>
          </Stack>
          {page.isError && (
            <Alert severity="error">
              {t('hotelCatalogAdmin.loadError')}{' '}
              <Button
                disabled={save.isLoading}
                onClick={() => {
                  resetEditor();
                  page.refetch();
                }}
              >
                {t('hotelCatalogAdmin.reload')}
              </Button>
            </Alert>
          )}
          {page.isFetching ? (
            <Typography>{t('hotelCatalogAdmin.loading')}</Typography>
          ) : (
            !page.isError && (
              <>
                <Typography variant="body2" color="text.secondary">
                  {t(
                    included
                      ? complete
                        ? 'hotelDestinationOrder.count'
                        : 'hotelDestinationOrder.range'
                      : complete
                        ? 'hotelDestinationMembership.disabledCount'
                        : 'hotelDestinationMembership.disabledRange',
                    {
                      count: rows.length,
                      start: rows.length ? offset + 1 : 0,
                      end: offset + rows.length,
                    },
                  )}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'minmax(0, 1fr)',
                      md: selected || adding ? 'minmax(0, 1fr) 300px' : 'minmax(0, 1fr)',
                    },
                    gap: 2,
                    alignItems: 'start',
                  }}
                >
                  <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 2 }}>
                      <Typography variant="h6">
                        {t(
                          previewing
                            ? 'hotelDestinationOrder.previewTitle'
                            : included
                              ? 'hotelDestinationOrder.currentOrder'
                              : 'hotelDestinationMembership.disabled',
                        )}
                      </Typography>
                      {previewing && (
                        <Chip
                          size="small"
                          color="primary"
                          variant="outlined"
                          label={t('hotelDestinationOrder.preview')}
                        />
                      )}
                    </Stack>
                    {previewing && !complete && (
                      <Alert severity="info" sx={{ mx: 2, mb: 2 }}>
                        {t('hotelDestinationOrder.partialPreview')}
                      </Alert>
                    )}
                    <TableContainer>
                      <Table size="small" sx={{ '& .MuiTableCell-root': { px: { xs: 1, sm: 2 } } }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              {t(
                                previewing && !complete
                                  ? 'hotelDestinationOrder.pagePosition'
                                  : 'hotelDestinationOrder.position',
                              )}
                            </TableCell>
                            <TableCell>{t('hotelDestinationOrder.hotel')}</TableCell>
                            <TableCell align="right">{t('hotelDestinationOrder.priority')}</TableCell>
                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }} />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {preview.map((row, index) => (
                            <TableRow key={row.hotel_id} selected={selected?.hotel_id === row.hotel_id}>
                              <TableCell>{index + 1 + (previewing && !complete ? 0 : offset)}</TableCell>
                              <TableCell>
                                <Typography variant="body2">{row.hotel_display_name}</Typography>
                                <Button
                                  size="small"
                                  href={`${catalogAdminPath}/hotels/${encodeURIComponent(row.hotel_id!)}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  aria-label={t('hotelDestinationOrder.contentHotel', { hotel: row.hotel_display_name })}
                                >
                                  {t('hotelDestinationOrder.content')}
                                </Button>
                                <Button
                                  size="small"
                                  sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
                                  disabled={blocked || selected?.hotel_id === row.hotel_id}
                                  onClick={() => choose(rows.find((original) => original.hotel_id === row.hotel_id)!)}
                                  aria-label={t('hotelDestinationOrder.editHotel', { hotel: row.hotel_display_name })}
                                >
                                  {t('hotelDestinationOrder.edit')}
                                </Button>
                                <Button
                                  size="small"
                                  sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
                                  disabled={blocked || selected?.hotel_id === row.hotel_id}
                                  onClick={() =>
                                    choose(
                                      rows.find((original) => original.hotel_id === row.hotel_id)!,
                                      row.enabled ? 'deactivate' : 'reactivate',
                                    )
                                  }
                                  aria-label={t(
                                    row.enabled
                                      ? 'hotelDestinationMembership.deactivateHotel'
                                      : 'hotelDestinationMembership.reactivateHotel',
                                    { hotel: row.hotel_display_name },
                                  )}
                                >
                                  {t(
                                    row.enabled
                                      ? 'hotelDestinationMembership.deactivate'
                                      : 'hotelDestinationMembership.reactivate',
                                  )}
                                </Button>
                              </TableCell>
                              <TableCell align="right">{row.rank}</TableCell>
                              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                <Button
                                  size="small"
                                  disabled={blocked || selected?.hotel_id === row.hotel_id}
                                  onClick={() => choose(rows.find((original) => original.hotel_id === row.hotel_id)!)}
                                  aria-label={t('hotelDestinationOrder.editHotel', { hotel: row.hotel_display_name })}
                                >
                                  {t('hotelDestinationOrder.edit')}
                                </Button>
                                <Button
                                  size="small"
                                  disabled={blocked || selected?.hotel_id === row.hotel_id}
                                  onClick={() =>
                                    choose(
                                      rows.find((original) => original.hotel_id === row.hotel_id)!,
                                      row.enabled ? 'deactivate' : 'reactivate',
                                    )
                                  }
                                  aria-label={t(
                                    row.enabled
                                      ? 'hotelDestinationMembership.deactivateHotel'
                                      : 'hotelDestinationMembership.reactivateHotel',
                                    { hotel: row.hotel_display_name },
                                  )}
                                >
                                  {t(
                                    row.enabled
                                      ? 'hotelDestinationMembership.deactivate'
                                      : 'hotelDestinationMembership.reactivate',
                                  )}
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {rows.length === 0 && (
                      <Typography sx={{ p: 2 }}>
                        {t(included ? 'hotelDestinationOrder.empty' : 'hotelDestinationMembership.emptyDisabled')}
                      </Typography>
                    )}
                  </Paper>
                  {adding && (
                    <AddDestinationHotel
                      key={destination.id}
                      destination={destination}
                      onChoose={choose}
                      onClose={resetEditor}
                    />
                  )}
                  {selected && (
                    <Paper
                      variant="outlined"
                      sx={{ p: 2 }}
                      component="form"
                      onSubmit={(event) => {
                        event.preventDefault();
                        if (inFlight.current || blocked || !changed || !validPriorityReason(reason)) return;
                        inFlight.current = true;
                        save.mutate(selected);
                      }}
                    >
                      <Stack spacing={2}>
                        <Typography variant="overline" color="primary">
                          {t(
                            mode === 'priority'
                              ? 'hotelDestinationOrder.editPriority'
                              : `hotelDestinationMembership.${mode}`,
                          )}
                        </Typography>
                        <Typography variant="h6">{selected.hotel_display_name}</Typography>
                        <Typography variant="caption" sx={{ overflowWrap: 'anywhere' }}>
                          {t('hotelDestinationMembership.reference', { id: selected.hotel_id })}
                        </Typography>
                        {(mode === 'add' || mode === 'reactivate') && (
                          <Stack spacing={1}>
                            <Typography variant="body2">
                              {t('hotelDestinationMembership.targetDestination', {
                                destination: destination.label,
                                id: selected.geo_place_id,
                              })}
                            </Typography>
                            {selectedCandidate && (
                              <Typography variant="body2">
                                {t('hotelDestinationMembership.hotelLocation', {
                                  country: selectedCandidate.country_code || t('hotelDestinationMembership.unknownLocation'),
                                  locality: selectedCandidate.locality || t('hotelDestinationMembership.unknownLocation'),
                                })}
                              </Typography>
                            )}
                            <Typography variant="body2">
                              {t(mode === 'add'
                                ? 'hotelDestinationMembership.reviewAdd'
                                : 'hotelDestinationMembership.reviewReactivate')}
                            </Typography>
                          </Stack>
                        )}
                        {mode !== 'priority' && (
                          <Alert severity="info">
                            {t(
                              mode === 'deactivate'
                                ? 'hotelDestinationMembership.deactivateHelp'
                                : 'hotelDestinationMembership.includeHelp',
                              { destination: destination.label },
                            )}
                          </Alert>
                        )}
                        <TextField
                          autoFocus
                          label={t('hotelDestinationOrder.priority')}
                          type="number"
                          value={rank}
                          disabled={blocked || mode === 'deactivate'}
                          onChange={(event) => setRank(event.target.value)}
                          inputProps={{ min: 0, max: 2147483647, step: 1 }}
                          helperText={t('hotelDestinationOrder.priorityHelp')}
                          error={!validPriority(rank)}
                        />
                        {mode === 'priority' && included && (
                          <Box aria-live="polite" sx={{ bgcolor: 'action.hover', p: 1.5, borderRadius: 1 }}>
                            <Typography>
                              {t(
                                complete
                                  ? 'hotelDestinationOrder.previewPosition'
                                  : 'hotelDestinationOrder.previewPagePosition',
                                { position: previewPosition, count: rows.length },
                              )}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t(changed ? 'hotelDestinationOrder.moves' : 'hotelDestinationOrder.unchanged', {
                                before: oldPosition,
                                after: previewPosition,
                              })}
                            </Typography>
                          </Box>
                        )}
                        <TextField
                          label={t('hotelCatalogAdmin.reason')}
                          value={reason}
                          onChange={(event) => setReason(event.target.value)}
                          disabled={blocked}
                          inputProps={{ maxLength: 512 }}
                          helperText={t('hotelDestinationOrder.reasonHelp')}
                          error={reason.length > 0 && !validPriorityReason(reason)}
                        />
                        {save.isError && (
                          <Alert severity="error">
                            {t(
                              isSelectionConflict(save.error)
                                ? 'hotelCatalogAdmin.conflict'
                                : 'hotelDestinationOrder.saveUncertain',
                            )}
                          </Alert>
                        )}
                        <Stack direction="row" spacing={1}>
                          <Button disabled={blocked} onClick={resetEditor}>
                            {t('hotelDestinationOrder.discard')}
                          </Button>
                          {save.isError ? (
                            <Button
                              onClick={() => {
                                resetEditor();
                                page.refetch();
                              }}
                            >
                              {t('hotelCatalogAdmin.reload')}
                            </Button>
                          ) : (
                            <Button
                              variant="contained"
                              type="submit"
                              disabled={save.isLoading || !changed || !validPriorityReason(reason)}
                            >
                              {t(
                                mode === 'priority'
                                  ? 'hotelDestinationOrder.save'
                                  : mode === 'add'
                                    ? 'hotelDestinationMembership.confirmAdd'
                                    : mode === 'reactivate'
                                      ? 'hotelDestinationMembership.confirmReactivate'
                                      : 'hotelDestinationMembership.deactivate',
                              )}
                            </Button>
                          )}
                        </Stack>
                      </Stack>
                    </Paper>
                  )}
                </Box>
              </>
            )
          )}
          <Stack direction="row" spacing={1}>
            <Button
              disabled={blocked || page.isFetching || offset === 0}
              onClick={() =>
                change(() => {
                  resetEditor();
                  setOffset((v) => Math.max(0, v - membershipPageSize));
                })
              }
            >
              {t('hotelCatalogAdmin.previous')}
            </Button>
            <Button
              disabled={
                blocked ||
                page.isFetching ||
                page.isError ||
                !page.data?.meta.has_more ||
                offset >= membershipOffsetLimit
              }
              onClick={() =>
                change(() => {
                  resetEditor();
                  setOffset((v) => v + membershipPageSize);
                })
              }
            >
              {t('hotelCatalogAdmin.next')}
            </Button>
          </Stack>
          {!page.isFetching && page.data?.meta.has_more && offset >= membershipOffsetLimit && (
            <Alert severity="info">{t('hotelDestinationOrder.pageLimit')}</Alert>
          )}
        </>
      )}
      <Typography variant="body2" color="text.secondary">
        {t('hotelDestinationOrder.searchHelp')}
      </Typography>
      <Dialog open={!!pendingChange} onClose={() => setPendingChange(null)}>
        <DialogTitle>{t('hotelDestinationOrder.discardTitle')}</DialogTitle>
        <DialogContent>{t('hotelDestinationOrder.discardBody')}</DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingChange(null)}>{t('hotelDestinationOrder.keepEditing')}</Button>
          <Button
            onClick={() => {
              pendingChange?.();
              setPendingChange(null);
            }}
          >
            {t('hotelDestinationOrder.discard')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
